import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { meApi, setActiveCustomerId } from '../utils/api';

/**
 * Header dropdown that lets a portal user switch between practice
 * locations they have access to (e.g. one CFO covering two practices).
 *
 * Renders NOTHING when the user has access to only one customer — the
 * default case for nearly everyone.
 *
 * Picking a customer:
 *   - Persists the selection to localStorage (read by authorizedFetch
 *     into the X-Macula-Customer-Id header)
 *   - Hard-reloads the page so every cached API response in the SPA is
 *     re-fetched in the new customer's scope. Cleaner than threading a
 *     "customer changed" event through every component.
 */
export default function CustomerSwitcher() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await meApi.getAccessibleCustomers();
        if (!cancelled) setData(d);
      } catch (e) {
        // Still renders nothing (the switcher is optional chrome), but do NOT
        // fail silently: a swallowed error here is indistinguishable from
        // "this user only has one practice", which made a disappearing
        // switcher impossible to diagnose.
        console.warn('[CustomerSwitcher] could not load accessible customers:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!data || !data.customers || data.customers.length < 2) return null;

  const current = data.customers.find(c => c.is_effective) || data.customers[0];

  const pick = (customerId) => {
    if (customerId === data.effective_customer_id) {
      setOpen(false);
      return;
    }
    setActiveCustomerId(customerId);
    // Full reload so every page's in-flight data refetches in the new scope.
    window.location.reload();
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 12 }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px',
          background: 'white',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
          color: '#0f172a',
          maxWidth: 280,
        }}
        title="Switch practice"
      >
        <Building2 size={14} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {current.name}
        </span>
        <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 260,
            background: 'white',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '6px 12px', fontSize: 11, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: '1px solid #f1f5f9',
          }}>
            Switch practice
          </div>
          {data.customers.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c.id)}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px',
                background: c.is_effective ? '#eff6ff' : 'white',
                border: 'none',
                borderTop: '1px solid #f1f5f9',
                cursor: 'pointer',
                fontSize: 13,
                color: '#0f172a',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { if (!c.is_effective) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { if (!c.is_effective) e.currentTarget.style.background = 'white'; }}
            >
              <Building2 size={14} style={{ color: c.is_effective ? '#0369a1' : '#64748b' }} />
              <span style={{ flex: 1 }}>{c.name}</span>
              {c.is_effective && <span style={{ fontSize: 11, color: '#0369a1', fontWeight: 600 }}>Current</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
